// Tooltip copy explaining HOW each Channel and Platform is identified, so a
// reader can reconcile the report against GA4 (channels) or understand the
// app's own source-to-platform normalization (platforms).
//
// CHANNEL_INFO describes GA4's *default channel grouping* — the app passes
// `sessionDefaultChannelGroup` through verbatim, so these are GA4's documented
// rules, not app logic.
//
// PLATFORM_INFO is DERIVED from the real classifier (`SOURCE_TO_PLATFORM` in
// `@shared/utm/types`) by reversing it into platform → sorted-unique synonyms,
// so the tooltip copy can never drift from the code that actually classifies.

import type { ReactNode } from "react";
import { SOURCE_TO_PLATFORM } from "@shared/utm/types";
import type { CanonicalPlatform } from "@shared/utm/types";

// GA4 default-channel-grouping descriptions. Concise, accurate to GA4's
// documented rules. Rendered as plain text.
export const CHANNEL_INFO: Record<string, ReactNode> = {
  Direct:
    "No referrer or campaign tags, so GA4 couldn't attribute a source. Usually typed URLs, bookmarks, or untagged links.",
  "Organic Search":
    "Unpaid traffic from a search engine (medium 'organic', or a recognised search source).",
  "Paid Search": "Search-engine traffic with a paid medium (cpc, ppc, or a 'paid' medium).",
  "Organic Social": "Unpaid traffic from a recognised social network, or medium 'social'.",
  "Paid Social": "Social-network traffic with a paid medium (cpc, ppc, or 'paid').",
  Referral: "Traffic from another website's link (medium 'referral').",
  Email: "Traffic tagged with an email source or medium.",
  Display: "Display or banner ads (medium 'display', 'banner', 'cpm', or 'interstitial').",
  "Cross-network":
    "Ads served across Google's networks (campaign tagged 'cross-network', e.g. Performance Max).",
  "Paid Other": "Paid traffic (a paid medium) that doesn't fit shopping, search, social, or video.",
  "Paid Shopping": "Shopping-site traffic, or a shopping campaign, with a paid medium.",
  "Paid Video": "Video-site traffic with a paid medium.",
  "Organic Shopping": "Unpaid traffic from a shopping site, or a shopping-tagged campaign.",
  "Organic Video": "Unpaid traffic from a video site (e.g. YouTube), or a 'video' medium.",
  Affiliates: "Traffic with the medium 'affiliate'.",
  Audio: "Traffic with the medium 'audio' (e.g. podcast ads).",
  SMS: "Traffic with the source or medium 'sms'.",
  "Mobile Push Notifications":
    "Traffic from mobile push (medium ending in 'push', or containing 'mobile'/'notification').",
  Unassigned:
    "GA4 couldn't match the visit to any channel rule. Usually missing or non-standard UTM tags.",
};

// Fallback for any channel string GA4 emits that isn't in the map above.
export const defaultChannelInfo =
  "Assigned by GA4's default channel grouping from the visit's source, medium and campaign.";

// Shared trailing note appended to every platform body.
const PLATFORM_NOTE =
  "Only Organic/Paid Social sessions are included; utm_source is used first, then the referrer.";

// Reverse the synonym map into platform → sorted unique synonyms. Built once
// at module load. "Other Social" never appears here (it's the fallback with no
// synonyms), so it's handled explicitly below.
const PLATFORM_SYNONYMS: Partial<Record<CanonicalPlatform, string[]>> = (() => {
  const acc: Partial<Record<CanonicalPlatform, Set<string>>> = {};
  for (const [source, platform] of Object.entries(SOURCE_TO_PLATFORM)) {
    (acc[platform] ??= new Set<string>()).add(source);
  }
  const out: Partial<Record<CanonicalPlatform, string[]>> = {};
  for (const [platform, set] of Object.entries(acc) as [CanonicalPlatform, Set<string>][]) {
    out[platform] = Array.from(set).sort();
  }
  return out;
})();

// Build the tooltip body for a single platform. "Other Social" is the
// fallback (no synonyms); every other platform lists its sorted synonyms
// from the reverse map plus the shared note as a muted second line. Kept as
// an inline arrow (not a named/exported function) so react-refresh doesn't
// treat this module as a component module.
const platformBody = (platform: CanonicalPlatform): ReactNode => {
  const sentence =
    platform === "Other Social"
      ? "Social visits whose source didn't match a known platform."
      : `Counted as ${platform} when the session source or utm_source is one of: ${(
          PLATFORM_SYNONYMS[platform] ?? []
        ).join(", ")}.`;
  return (
    <span>
      {sentence}
      <span className="mt-1 block text-neutral-300">{PLATFORM_NOTE}</span>
    </span>
  );
};

// One entry per canonical platform. The keys are the canonical platform
// names (necessarily spelled out — they're the union members), but every
// body's SYNONYM LIST is derived from `PLATFORM_SYNONYMS` (the reverse of
// `SOURCE_TO_PLATFORM`) via `platformBody`, so the lists can never drift
// from the classifier. Written as an object literal (rather than
// `Object.fromEntries`) so react-refresh recognises it as a constant export.
export const PLATFORM_INFO: Record<CanonicalPlatform, ReactNode> = {
  Instagram: platformBody("Instagram"),
  Facebook: platformBody("Facebook"),
  LinkedIn: platformBody("LinkedIn"),
  TikTok: platformBody("TikTok"),
  Pinterest: platformBody("Pinterest"),
  YouTube: platformBody("YouTube"),
  Threads: platformBody("Threads"),
  X: platformBody("X"),
  "Google Business": platformBody("Google Business"),
  "Other Social": platformBody("Other Social"),
};
